import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DrawingRevisionDetail } from '../../../../../../../../shared/models/drawing-sheet';
import { ProjectDrawingsService } from '../../../../../../../../shared/services/project-drawings.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { DrawingsAnalytics } from '../drawings-analytics';
import { revisionName } from '../drawing-overlay';

export interface CompareTransform {
  scale: number;
  x: number;
  y: number;
}

/** W67: the last Side by side / Overlay choice, per browser */
export const COMPARE_MODE_KEY = 'drawings.compare.mode';
export type CompareMode = 'side' | 'overlay';

/** The last mode used in this browser; Overlay on a first visit (Sri) or when storage is unavailable */
function readMode(): CompareMode {
  try {
    return localStorage.getItem(COMPARE_MODE_KEY) === 'side' ? 'side' : 'overlay';
  } catch {
    return 'overlay';
  }
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const STEP = 1.5;

/**
 * Two revisions of one sheet, side by side (one transform drives both page images, so zooming or panning either side
 * moves the other the same way) or overlaid (W67: removed red, added blue, unchanged grey).
 */
@Component({
  selector: 'app-drawing-compare',
  templateUrl: './drawing-compare.component.html',
  styleUrls: ['./drawing-compare.component.scss'],
  standalone: false,
})
export class DrawingCompareComponent implements OnChanges, OnDestroy {
  /** The app's toast (Figma 11893:79195): CommonService.notification, no action button */
  private readonly notify = inject(CommonService);
  /** W48: PostHog events */
  private readonly track = inject(DrawingsAnalytics);
  @Input() projectId: string;
  /** The newer revision */
  @Input() revisionUuid: string;
  /** The revision it supersedes */
  @Input() againstUuid: string;
  /** The back button's name when compare was opened from elsewhere (set review's Preview changes) */
  @Input() backLabelOverride: string | null = null;
  /** Inside the set review's page review: no page header of its own (the review has one) */
  @Input() embedded = false;
  /** Opens on this view instead of the last one used (the set review's page review opens on Overlay) */
  @Input() set startMode(mode: CompareMode | null) {
    if (mode) {
      this.mode = mode;
    }
  }
  @Output() back = new EventEmitter<void>();

  /** "Rev B", or "Draft" for set review's Preview changes */
  readonly revisionName = revisionName;
  older: DrawingRevisionDetail | null = null;
  newer: DrawingRevisionDetail | null = null;
  /** W67: Side by side, or the Overlay (removed red, added blue); the last choice opens */
  mode: CompareMode = readMode();
  transform: CompareTransform = { scale: 1, x: 0, y: 0 };
  @ViewChild('firstPane') private firstPane?: ElementRef<HTMLElement>;
  private drag: { x: number; y: number } | null = null;
  private readonly reset = new Subject<void>();
  private readonly onDestroy = new Subject<void>();

  constructor(private api: ProjectDrawingsService) {}

  /** The header's title: "Compare 5 · PROJECT LAYOUT" (the revisions are the line under it) */
  get compareTitle(): string {
    // A draft page (set review's Preview changes) has no sheet yet: the issued revision names it
    const sheet = this.newer?.sheet ?? this.older?.sheet;
    return sheet ? 'Compare ' + [sheet.number, sheet.title].filter(Boolean).join(' · ') : 'Compare';
  }

  /**
   * Wrong-drawing guard: "This draft looks different from Rev B (54% alike). Check it's the right drawing." Only when
   * the API measured the right-hand revision against the one on the left, and found it low.
   */
  get similarityNote(): string {
    const s = this.newer?.similarity;
    if (!s?.low || !this.older || s.against_uuid !== this.older.uuid) {
      return '';
    }
    const name = this.newer.status === 'draft' ? 'This draft' : revisionName(this.newer);
    return `${name} looks different from ${revisionName(this.older)} (${Math.round(s.score * 100)}% alike). Check it's the right drawing.`;
  }

  get transformCss(): string {
    const t = this.transform;
    return `translate(${t.x}px, ${t.y}px) scale(${t.scale})`;
  }

  get canZoomIn(): boolean {
    return this.transform.scale < MAX_SCALE;
  }

  get canZoomOut(): boolean {
    return this.transform.scale > MIN_SCALE;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.projectId && !changes.revisionUuid && !changes.againstUuid) {
      return;
    }
    this.reset.next();
    this.resetView();
    if (!this.revisionUuid || !this.againstUuid) {
      return;
    }
    forkJoin([this.api.getRevision(this.projectId, this.againstUuid), this.api.getRevision(this.projectId, this.revisionUuid)])
      .pipe(takeUntil(this.reset), takeUntil(this.onDestroy))
      .subscribe({
        next: ([older, newer]) => {
          this.older = older;
          this.newer = newer;
        },
        error: e => {
          this.track.error('compare', e);
          this.notify.notification(this.api.failureMessage(e?.error?.code, this.api.errorMessage(e)), 'error');
        },
      });
  }

  ngOnDestroy(): void {
    this.reset.complete();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  setMode(mode: CompareMode): void {
    if (mode === this.mode) {
      return;
    }
    this.mode = mode;
    this.track.track('compare_mode_changed', { mode });
    try {
      localStorage.setItem(COMPARE_MODE_KEY, mode);
    } catch {
      // Private mode or storage full: the choice lasts this visit only
    }
  }

  /** Zooms by `factor` keeping the pane point (px) under the cursor still. Back at 1× the page fits again. */
  zoomAt(factor: number, cx: number, cy: number): void {
    const t = this.transform;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, t.scale * factor));
    if (scale === MIN_SCALE) {
      this.transform = { scale, x: 0, y: 0 };
      return;
    }
    const k = scale / t.scale;
    this.transform = { scale, x: cx - (cx - t.x) * k, y: cy - (cy - t.y) * k };
  }

  panBy(dx: number, dy: number): void {
    this.transform = { ...this.transform, x: this.transform.x + dx, y: this.transform.y + dy };
  }

  resetView(): void {
    this.transform = { scale: 1, x: 0, y: 0 };
  }

  zoomBy(direction: 1 | -1): void {
    const pane = this.firstPane?.nativeElement;
    this.zoomAt(direction > 0 ? STEP : 1 / STEP, (pane?.clientWidth ?? 0) / 2, (pane?.clientHeight ?? 0) / 2);
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.zoomAt(event.deltaY < 0 ? 1.25 : 0.8, event.clientX - rect.left, event.clientY - rect.top);
  }

  onPointerDown(event: PointerEvent): void {
    this.drag = { x: event.clientX, y: event.clientY };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.drag) {
      return;
    }
    this.panBy(event.clientX - this.drag.x, event.clientY - this.drag.y);
    this.drag = { x: event.clientX, y: event.clientY };
  }

  onPointerUp(): void {
    this.drag = null;
  }

  /** Arrow keys pan, + and - zoom, 0 fits. */
  onKey(event: KeyboardEvent): void {
    const pans: Record<string, [number, number]> = { ArrowLeft: [40, 0], ArrowRight: [-40, 0], ArrowUp: [0, 40], ArrowDown: [0, -40] };
    if (pans[event.key]) {
      this.panBy(...pans[event.key]);
    } else if (event.key === '+' || event.key === '=') {
      this.zoomBy(1);
    } else if (event.key === '-') {
      this.zoomBy(-1);
    } else if (event.key === '0') {
      this.resetView();
    } else {
      return;
    }
    event.preventDefault();
  }
}
